import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import InternalEvents from '@lib/core/src/enums/InternalEvents';
import SourceQueryParam from '@lib/core/src/enums/SourceQueryParam';
import ToastVariant from '@lib/core/src/enums/ToastVariant';
import TokenType from '@lib/core/src/enums/TokenType';
import TokensInternalMessage from '@lib/core/src/types/TokensInternalMessage';
import LogRocket from 'logrocket';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import {
  SignInMfaRequest,
  SignInRequest,
  SignUpRequest,
  useAccountSignIn,
  useGetUserDetails,
  useGetUserMfaSettings,
  useMfaSignIn,
  UserDetailsType,
  useSignIn,
  useSignOut,
  useSignUp
} from '@/api/generated';

import { LoginContext } from './useLoginContext';

const LoginContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [signInLoading, setSignInLoading] = useState(false);
  const idToken = localStorage.getItem(TokenType.ID);

  const { mutateAsync: authSignin } = useSignIn();
  const { mutateAsync: authSignup, isPending: signUpLoading } = useSignUp();
  const { mutateAsync: authSignOut } = useSignOut();
  const { mutateAsync: authMfaSignin, isPending: isLoadingAuthMfaSignin } = useMfaSignIn();

  const [source] = useSearchParams();
  const editorExtensionId = 'kdbmjlfdejobblcflmlcdcknncbnfdej';
  useEffect(() => {
    // TODO: The ID of the extension we want to talk to. CHANGE THIS TO A SECRET

    if (window.chrome && chrome.runtime) {
      chrome.runtime.sendMessage(editorExtensionId, {
        type: InternalEvents.SEND_TOKENS,
        message: { idToken }
      } as TokensInternalMessage);
    }
  }, [idToken]);

  const {
    data: userDetailsResponse,
    refetch: refetchUserDetails,
    isLoading: userDetailsLoading
  } = useGetUserDetails({
    query: {
      queryKey: ['userDetails', idToken],
      enabled: !!idToken
    }
  });

  useEffect(() => {
    if (userDetailsResponse?.items?.[0].id) {
      const currentUser = userDetailsResponse.items[0];
      if (
        currentUser &&
        currentUser?.email &&
        currentUser?.id &&
        currentUser?.currentAccountId &&
        currentUser?.type
      ) {
        LogRocket.identify(currentUser.id, {
          name: currentUser.firstName + ' ' + currentUser.lastName,
          email: currentUser.email,
          accountId: currentUser.currentAccountId,
          type: currentUser.type
        });
      }
    }
  }, [userDetailsResponse?.items]);

  const { data: userMfaSettingsResponse, refetch: getUserMfaSettings } = useGetUserMfaSettings({
    query: {
      queryKey: ['userMfaSettings'],
      enabled: false
    }
  });

  const userMfaSettings = useMemo(
    () => userMfaSettingsResponse?.items?.[0],
    [userMfaSettingsResponse]
  );

  const { mutateAsync: authAccountSignin } = useAccountSignIn();

  const isAuthenticated = useMemo(() => !!idToken, [idToken]);

  const currentUser = useMemo(() => userDetailsResponse?.items?.[0] ?? null, [userDetailsResponse]);

  const currentAccount = currentUser?.accounts?.find(
    item => item.id === currentUser.currentAccountId
  );

  const setTokens = useCallback(
    (tokens?: {
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
      socketToken?: string;
    }) => {
      const { idToken, accessToken, refreshToken, socketToken } = tokens || {};
      if (idToken) {
        localStorage.setItem(TokenType.ID, idToken);
      } else {
        localStorage.removeItem(TokenType.ID);
      }
      if (accessToken) {
        localStorage.setItem(TokenType.ACCESS, accessToken);
      } else {
        localStorage.getItem(TokenType.ACCESS);
      }
      if (refreshToken) {
        localStorage.setItem(TokenType.REFRESH, refreshToken);
      } else {
        localStorage.removeItem(TokenType.REFRESH);
      }
      if (socketToken) {
        localStorage.setItem(TokenType.SOCKET, socketToken);
      } else {
        localStorage.removeItem(TokenType.SOCKET);
      }
    },
    []
  );

  const accountSignin = useCallback(
    async (accountId: number) => {
      if (!isAuthenticated) return;
      const { items } = await authAccountSignin({ data: { accountId } });
      const { ...tokens } = items?.[0] ?? {};
      setTokens(tokens);
      // TODO: The ID of the extension we want to talk to. CHANGE THIS TO A SECRET
      const editorExtensionId = 'kdbmjlfdejobblcflmlcdcknncbnfdej';
      // Check if extension is installed
      if (window.chrome && chrome.runtime) {
        // Make a request:
        chrome.runtime.sendMessage(editorExtensionId, {
          type: InternalEvents.SEND_TOKENS,
          message: tokens
        } as TokensInternalMessage);
      }
    },
    [authAccountSignin, isAuthenticated, setTokens]
  );

  const signIn = useCallback(
    async (data: SignInRequest) => {
      setSignInLoading(true);

      try {
        const { items } = await authSignin({ data });
        const signInDetails = items?.[0];

        if (signInDetails && signInDetails.idToken) {
          const { ...tokens } = signInDetails;
          if (source.get('source') === SourceQueryParam.EXTENSION) {
            const tokensMessage = {
              type: 'LOGIN_SUCCESS',
              payload: tokens // { idToken, accessToken, refreshToken, socketToken }
            };

            if (window.opener) {
              window.opener.postMessage(tokensMessage, '*');
              window.close();
            } else if (window.chrome && chrome.runtime) {
              chrome.runtime.sendMessage(
                editorExtensionId,
                { type: InternalEvents.SEND_TOKENS, message: tokens },
                response => {
                  if (response.success) {
                    window.close();
                  } else {
                    enqueueSnackbar({
                      variant: 'custom',
                      customProps: {
                        title: "Couldn't send tokens to extension",
                        caption:
                          'We could not send the tokens to the extension. Please try again later or contact support.',
                        variant: ToastVariant.ERROR,
                        cta: { onClick: () => closeSnackbar() }
                      }
                    });
                  }
                }
              );
            }
          }
          setTokens(tokens);
          await refetchUserDetails();
        }
        setSignInLoading(false);
        return signInDetails;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        setSignInLoading(false);
        setTokens();
        enqueueSnackbar({
          variant: 'custom',
          customProps: {
            title: 'Sign In Failed',
            caption:
              'We could not sign you in. Please check your email and password and try again.',
            variant: ToastVariant.ERROR,
            cta: {
              onClick: () => {
                closeSnackbar();
              }
            }
          }
        });
      }
    },
    [authSignin, refetchUserDetails, setTokens, source]
  );

  const signUp = useCallback(
    async (data: SignUpRequest) => {
      try {
        const { items } = await authSignup({ data });
        const signInDetails = items?.[0];

        if (signInDetails && signInDetails.idToken) {
          const { ...tokens } = items[0];
          setTokens(tokens);
          await refetchUserDetails();
        }
        setSignInLoading(false);
        return signInDetails;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        setSignInLoading(false);
        setTokens();
        const id = enqueueSnackbar('Error Setting Password', {
          autoHideDuration: 3000,
          variant: 'custom',
          preventDuplicate: true,
          customProps: {
            caption:
              'There was an error setting your password, please try again or contact support.',
            variant: ToastVariant.ERROR,
            cta: {
              onClick: () => {
                closeSnackbar(id);
              }
            }
          }
        });
      }
    },
    [authSignup, refetchUserDetails, setTokens]
  );

  const mfaSignIn = useCallback(
    async (data: SignInMfaRequest) => {
      setSignInLoading(true);

      try {
        const { items } = await authMfaSignin({ data });
        const { ...tokens } = items?.[0] ?? {};
        setTokens(tokens);
        await refetchUserDetails();
      } catch (error) {
        setSignInLoading(false);
        throw error;
      }

      setSignInLoading(false);
    },
    [authMfaSignin, refetchUserDetails, setTokens]
  );

  const signOut = useCallback(() => {
    authSignOut();
    setTokens();
  }, [authSignOut, setTokens]);

  useEffect(() => {
    if (
      isAuthenticated &&
      currentUser?.accounts?.length &&
      currentUser?.type &&
      currentUser?.type in [UserDetailsType.admin, UserDetailsType.superAdmin, UserDetailsType.jp]
    ) {
      accountSignin(currentUser.accounts[0].id).then(() => refetchUserDetails());
    }
  }, [accountSignin, currentUser, isAuthenticated, refetchUserDetails]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      signInLoading,
      userDetailsLoading,
      currentUser,
      currentAccount,
      signOut,
      signIn,
      signUp,
      signUpLoading,
      mfaSignIn,
      isLoadingAuthMfaSignin,
      userMfaSettings,
      getUserMfaSettings,
      refetchUserDetails
    }),
    [
      isAuthenticated,
      signInLoading,
      userDetailsLoading,
      currentUser,
      currentAccount,
      signOut,
      signIn,
      signUp,
      signUpLoading,
      mfaSignIn,
      isLoadingAuthMfaSignin,
      userMfaSettings,
      getUserMfaSettings,
      refetchUserDetails
    ]
  );

  return <LoginContext.Provider value={value}>{children}</LoginContext.Provider>;
};

export default LoginContextProvider;
